import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, Sparkles, Link2, RefreshCw, Tag as TagIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useGlossaryList,
  useGlossaryCategorias,
  useGlossaryTerm,
  useSeedGlossary,
  useCrossGlossary,
  type GlossaryTerm,
} from '@/hooks/useGlossary';
import { cn } from '@/lib/utils';

export function GlossarioPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const listQ = useGlossaryList({
    q: search,
    categoria: categoria ?? undefined,
  });
  const catQ = useGlossaryCategorias();
  const termQ = useGlossaryTerm(selectedSlug);
  const seed = useSeedGlossary();
  const cross = useCrossGlossary();

  const stats = listQ.data?.stats;
  const terms = listQ.data?.terms ?? [];
  const categorias = catQ.data?.categorias ?? [];

  const groupedByCategoria = useMemo(() => {
    const groups = new Map<string, GlossaryTerm[]>();
    for (const t of terms) {
      const cat = t.categoria ?? 'Sem categoria';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(t);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [terms]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <BookOpen className="h-6 w-6" />
            Glossário Imperatriz Tech
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Dicionário técnico do seu ecossistema — com cruzamento automático dos termos nos itens cadastrados.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => seed.mutate(undefined)}
            disabled={seed.isPending}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {seed.isPending ? 'Importando...' : 'Seed do vault'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => cross.mutate()}
            disabled={cross.isPending}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', cross.isPending && 'animate-spin')} />
            Cruzar com itens
          </Button>
        </div>
      </div>

      {seed.data && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4 text-sm text-emerald-900">
            Importados {seed.data.total} termos do vault ({seed.data.novos} novos, {seed.data.atualizados} atualizados).
          </CardContent>
        </Card>
      )}
      {cross.data && (
        <Card className="border-sky-200 bg-sky-50">
          <CardContent className="pt-4 text-sm text-sky-900">
            Cruzamento: {cross.data.termos_processados} termos processados, {cross.data.matches_novos}{' '}
            novos matches. Total: {cross.data.total_matches}.
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Termos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_termos ?? '—'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Categorias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_categorias ?? '—'}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Matches com itens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total_matches ?? '—'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Busca + filtro */}
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Busca por termo, analogia ou definição..."
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={categoria === null ? 'default' : 'outline'}
            onClick={() => setCategoria(null)}
          >
            Todos
          </Button>
          {categorias.map((c) => (
            <Button
              key={c.categoria}
              size="sm"
              variant={categoria === c.categoria ? 'default' : 'outline'}
              onClick={() => setCategoria(c.categoria)}
            >
              {c.categoria} <span className="ml-1 opacity-60">({c.total})</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Grid: lista + detalhe */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          {listQ.isLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
          {!listQ.isLoading && terms.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Nenhum termo encontrado.{' '}
                {stats?.total_termos === 0 && (
                  <>Clique em <strong>"Seed do vault"</strong> pra importar o glossário.</>
                )}
              </CardContent>
            </Card>
          )}
          {groupedByCategoria.map(([cat, items]) => (
            <div key={cat}>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {cat} <span className="opacity-60">({items.length})</span>
              </h2>
              <div className="grid gap-2 md:grid-cols-2">
                {items.map((t) => (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => setSelectedSlug(t.slug)}
                    className={cn(
                      'rounded-lg border bg-card px-4 py-3 text-left transition hover:shadow-md',
                      selectedSlug === t.slug && 'border-primary bg-primary/5',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{t.termo}</span>
                      {t.tag && <Badge variant="outline">{t.tag}</Badge>}
                    </div>
                    {t.o_que_e && (
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{t.o_que_e}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Detalhe */}
        <aside className="h-fit sticky top-6">
          {!selectedSlug && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Clique em um termo pra ver detalhes.
              </CardContent>
            </Card>
          )}
          {selectedSlug && termQ.isLoading && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">Carregando...</CardContent>
            </Card>
          )}
          {termQ.data && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle>{termQ.data.term.termo}</CardTitle>
                  {termQ.data.term.tag && (
                    <Badge variant="outline" className="ml-auto">
                      <TagIcon className="mr-1 h-3 w-3" />
                      {termQ.data.term.tag}
                    </Badge>
                  )}
                </div>
                {termQ.data.term.categoria && (
                  <p className="text-xs text-muted-foreground">{termQ.data.term.categoria}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {termQ.data.term.o_que_e && (
                  <div>
                    <div className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                      O que é
                    </div>
                    <p className="mt-1">{termQ.data.term.o_que_e}</p>
                  </div>
                )}
                {termQ.data.term.analogia && (
                  <div>
                    <div className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                      Analogia
                    </div>
                    <p className="mt-1 italic">{termQ.data.term.analogia}</p>
                  </div>
                )}
                {termQ.data.term.onde_usa && (
                  <div>
                    <div className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                      Onde você usa
                    </div>
                    <p className="mt-1">{termQ.data.term.onde_usa}</p>
                  </div>
                )}
                {termQ.data.items.length > 0 && (
                  <div>
                    <div className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                      <Link2 className="mr-1 inline h-3 w-3" />
                      Usado em {termQ.data.items.length} itens
                    </div>
                    <ul className="mt-2 space-y-1">
                      {termQ.data.items.slice(0, 10).map((i) => (
                        <li key={`${i.item_id}-${i.campo}`} className="text-xs">
                          <Link
                            to={`/items/${i.item_id}`}
                            className="text-primary hover:underline"
                          >
                            {i.nome}
                          </Link>
                          <span className="ml-1 text-muted-foreground">({i.tipo})</span>
                        </li>
                      ))}
                      {termQ.data.items.length > 10 && (
                        <li className="text-xs text-muted-foreground">
                          + {termQ.data.items.length - 10} outros...
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}
